import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Autocomplete,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useQueryClient, useMutation, useQuery } from "react-query";
import { formatFileSize } from "../../services/Utils";
import { getStsToken, getServerConfiguration } from "../../services/Image";
import {
  createCognitoAi,
  validateCognitoAiVersion,
} from "../../services/CognitoAi";
import { useToast } from "../toast";
import Constants from "../../services/Constants";

export default function CognitoAiFileUploadAdditionalInfoPane({
  fileName,
  fileType,
  fileSize,
  file,
  variants,
  onComplete,
  onBack,
  initialData = null,
}) {
  const { UPLOAD_STATUS } = Constants;
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(initialData?.version || "");
  const [productVariant, setProductVariant] = useState(
    initialData?.productVariant || "",
  );
  const [description, setDescription] = useState(
    initialData?.description || "",
  );

  useEffect(() => {
    if (initialData) {
      setVersion(initialData.version || "");
      setProductVariant(initialData.productVariant || "");
      setDescription(initialData.description || "");
    }
  }, [initialData]);
  const [errors, setErrors] = useState({});
  const [validating, setValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const [stsToken, setStsToken] = useState({
    accessKeyId: "",
    secretAccessKey: "",
    sessionToken: "",
  });
  const [cognitoAiBucket, setCognitoAiBucket] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceTimer = useRef(null);

  const isReupload = !!initialData;

  const checkVersionValidity = useCallback(async () => {
    // Skip version validation in reupload mode
    if (isReupload) return;
    if (!productVariant || !version) return;

    setValidating(true);
    setValidationMessage(null);

    try {
      const result = await validateCognitoAiVersion(productVariant, version);

      if (!result.isValid) {
        setValidationMessage({
          type: "error",
          text:
            result.message ||
            "This version already exists for this product variant",
        });
      } else if (result.message) {
        setValidationMessage({
          type: "warning",
          text: result.message,
        });
      } else {
        setValidationMessage(null);
      }
    } catch (error) {
      console.error("Version validation error:", error);
    } finally {
      setValidating(false);
    }
  }, [productVariant, version, isReupload]);

  useEffect(() => {
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Validate version when both productVariant and version are set
    // Skip in reupload mode
    if (
      !isReupload &&
      productVariant &&
      version &&
      variants.includes(productVariant)
    ) {
      debounceTimer.current = setTimeout(async () => {
        await checkVersionValidity();
      }, 500);
    } else {
      setValidationMessage(null);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [productVariant, version, variants, checkVersionValidity, isReupload]);

  // Fetch STS Token
  const { isLoading: isLoadingStsToken, isFetching: isFetchingStsToken } =
    useQuery("stsToken", getStsToken, {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const { data: token = {} } = data;
        setStsToken(token);
      },
      onError: (err) => {
        const {
          response: { data: errorData },
        } = err;
        if (err && errorData) {
          addToast({
            type: "error",
            message: errorData.message || "Failed to fetch STS token",
            autoClose: 3000,
          });
        }
      },
    });

  // Fetch Server Configuration
  useQuery("getServerConfig", getServerConfiguration, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const { data: serverConfig = {} } = data;
      setCognitoAiBucket(
        serverConfig.s3.COGNITO_AI_UPLOAD_BUCKET ||
          serverConfig.s3.IMAGE_UPLOAD_BUCKET,
      );
    },
    onError: (err) => {
      const {
        response: { data: errorData },
      } = err;
      if (err && errorData) {
        addToast({
          type: "error",
          message: errorData.message || "Failed to fetch server configuration",
          autoClose: 3000,
        });
      }
    },
  });

  // Create Cognito AI Upload Mutation
  const { mutate: mutateCognitoAi, isLoading: isCreating } = useMutation(
    createCognitoAi,
    {
      onSuccess: async ({ data }) => {
        // Pass all necessary data to start upload
        onComplete({
          version: version.trim(),
          productVariant: productVariant.trim(),
          description: description.trim(),
          uploadMetadata: data,
          stsToken,
          cognitoAiBucket,
        });
      },
      onError: (err) => {
        const {
          response: { data: errorData },
        } = err;
        if (err && errorData) {
          addToast({
            type: "error",
            message: errorData.message || "Failed to create upload",
            autoClose: 3000,
          });
        }
        setIsSubmitting(false);
      },
      onSettled: () => {
        queryClient.invalidateQueries("cognito-ai-uploads");
      },
    },
  );

  const validate = () => {
    const newErrors = {};

    if (!version.trim()) {
      newErrors.version = "Version is required";
    }

    if (!productVariant.trim()) {
      newErrors.productVariant = "Product variant is required";
    }

    // Check if validation message indicates an error
    if (validationMessage?.type === "error") {
      newErrors.version = validationMessage.text;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (isReupload) {
      // Reupload: skip creating a new record, use existing uploadId
      onComplete({
        uploadMetadata: initialData,
        stsToken,
        cognitoAiBucket,
      });
      return;
    }
    if (validate()) {
      setIsSubmitting(true);
      const payload = {
        fileName: fileName,
        fileType: fileType,
        fileSize: fileSize.toString(),
        version: version.trim(),
        productVariant: productVariant.trim(),
        description: description.trim(),
        uploadStatus: UPLOAD_STATUS.Started,
      };
      mutateCognitoAi(payload);
    }
  };

  const isSubmitDisabled = () => {
    if (isReupload) {
      return isLoadingStsToken || isFetchingStsToken || !cognitoAiBucket;
    }
    return (
      isSubmitting ||
      isCreating ||
      isLoadingStsToken ||
      isFetchingStsToken ||
      validating ||
      !cognitoAiBucket ||
      validationMessage?.type === "error"
    );
  };

  return (
    <Box sx={{ py: 2 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: "#f5f5f5" }}>
        <Typography variant="h6" gutterBottom>
          File Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              File Name
            </Typography>
            <Typography variant="body1">{fileName}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              File Type
            </Typography>
            <Typography variant="body1">{fileType}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              File Size
            </Typography>
            <Typography variant="body1">{formatFileSize(fileSize)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Upload Details
        {isReupload && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: 13 }}
          >
            These fields are read-only during reupload.
          </Typography>
        )}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 2 }}>
        <Autocomplete
          freeSolo
          options={variants || []}
          value={productVariant}
          disabled={isReupload}
          onChange={(event, newValue) => {
            setProductVariant(newValue || "");
            if (errors.productVariant) {
              setErrors({ ...errors, productVariant: null });
            }
          }}
          onInputChange={(event, newInputValue) => {
            setProductVariant(newInputValue);
            if (errors.productVariant) {
              setErrors({ ...errors, productVariant: null });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product Variant *"
              error={!!errors.productVariant}
              helperText={
                errors.productVariant ||
                "Select from existing or type a new variant"
              }
            />
          )}
        />

        <TextField
          label="Version *"
          value={version}
          onChange={(e) => {
            setVersion(e.target.value);
            if (errors.version) {
              setErrors({ ...errors, version: null });
            }
          }}
          fullWidth
          disabled={isReupload}
          error={!!errors.version}
          helperText={errors.version || "e.g., 1.0.0, v2.3.1"}
          placeholder="Enter version"
          InputProps={{
            endAdornment: !isReupload && validating && (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ),
          }}
        />

        {!isReupload && validationMessage && (
          <Alert severity={validationMessage.type} sx={{ mt: -1 }}>
            {validationMessage.text}
          </Alert>
        )}

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={4}
          disabled={isReupload}
          placeholder="Optional: Add a description for this upload"
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button variant="outlined" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={isSubmitDisabled()}
          startIcon={
            (isSubmitting || isCreating || isLoadingStsToken) && (
              <CircularProgress size={20} />
            )
          }
        >
          {isSubmitting || isCreating
            ? "Preparing Upload..."
            : isLoadingStsToken
              ? "Loading..."
              : "Start Upload"}
        </Button>
      </Box>
    </Box>
  );
}
